/* import __COLOCATED_TEMPLATE__ from './plan-card.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { MARKETING_PLAN_TRANSLATION_KEYS, PLAN_DATA } from 'embercom/lib/billing';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';

import type Plan from 'embercom/models/plan';
import type IntlService from 'ember-intl/services/intl';
import type Price from 'embercom/models/price';
import type QuoteService from 'embercom/services/quote-service';
import type Quote from 'embercom/models/quote';
import type CompellingOfferCampaignService from 'embercom/services/compelling-offer-campaign-service';
import { formatPriceValue } from 'embercom/lib/billing/format-price';

interface Args {
  plan: Plan;
  selectedPlanId: number;
  selectPlan: (planId: number) => void;
  isSinglePlanPaywall: boolean;
  showUpgradePathPlans: boolean;
  featureLessHeaders?: boolean;
  featureName?: string | null;
}

interface Signature {
  Args: Args;
  Element: HTMLElement;
}
export default class PlanCardComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare customerService: any;
  @service declare paywallService: any;
  @service declare quoteService: QuoteService;
  @service declare compellingOfferCampaignService: CompellingOfferCampaignService;
  @service declare appService: AppService;
  @service declare intercomEventService: any;

  private COMPACT_FEATURE_DISPLAY_LIMIT = 4;

  get isOnGraduatingTrial(): boolean {
    return this.args.plan.active && this.args.plan.activeTrialIsGraduating;
  }

  get isNonGraduatingTrial(): boolean {
    return (
      this.args.plan.active &&
      !this.args.plan.activeTrialIsGraduating &&
      !this.args.plan.billableCustomerPlan
    );
  }

  get shouldUseQuoteService(): boolean {
    return this.paywallService.shouldUseQuoteService(this.args.selectedPlanId);
  }

  get showLoader() {
    return this.shouldUseQuoteService && this.quoteService.loading;
  }

  get isInteractive(): boolean {
    return (
      !this.args.isSinglePlanPaywall &&
      !this.args.plan.billableCustomerPlan &&
      !this.isOnGraduatingTrial &&
      !this.isNonGraduatingTrial
    );
  }

  get isSelected(): boolean {
    return !this.args.isSinglePlanPaywall && this.args.selectedPlanId === this.args.plan.idAsNumber;
  }

  get planName(): string {
    if (this.planData?.planCardHeadingOverride) {
      return this.intl.t(this.planData.planCardHeadingOverride);
    }

    return this.appService.app.hasValueBasedPricing2
      ? this.args.plan.nameWithProduct
      : this.args.plan.name;
  }

  get planData(): any | undefined {
    return !this.shouldUseLegacyPlanData
      ? PLAN_DATA[this.args.plan.id]
      : MARKETING_PLAN_TRANSLATION_KEYS[this.args.plan.id];
  }

  private get shouldUseLegacyPlanData(): boolean {
    return !(this.appService.app.onPricing5 || this.appService.app.onFinForPlatforms);
  }

  get description(): string {
    return this.planData?.descriptionTranslationKey
      ? this.intl.t(this.planData.descriptionTranslationKey)
      : '';
  }

  get linkArticleText(): string {
    let translationKey = this.planData?.linkArticleTextTranslationKey;
    return translationKey
      ? this.intl.t(translationKey)
      : this.intl.t('paywalls.comparison.plan-cards.learn_more');
  }

  get marketingFeatureListHeading(): string {
    let marketingFeaturesHeading = this.planData.marketingFeaturesHeadingOverride
      ? this.intl.t(this.planData.marketingFeaturesHeadingOverride)
      : this.intl.t('paywalls.comparison.plan-cards.key_features');

    return marketingFeaturesHeading.toUpperCase();
  }

  get features(): Array<string> | undefined {
    let features = this.planData?.marketingFeaturesTranslationKeys;
    if (!features || !this.appService.app.canUseFeature('team-self-serve-ai-insights-addon')) {
      return features;
    }
    return features.map((feature: any) => {
      if (feature.title?.endsWith('.marketing_features.optimize')) {
        return { ...feature, title: `${feature.title}-recommendations` };
      }
      return feature;
    });
  }

  get featureTextClasses() {
    let classes = 'flex items-center flex-nowrap pb-1 ';
    if (this.args.isSinglePlanPaywall && !this.smallNumberOfFeatures) {
      classes += ' w-1/3';
    }
    return classes;
  }

  get smallNumberOfFeatures(): boolean {
    return this.features ? this.features.length <= this.COMPACT_FEATURE_DISPLAY_LIMIT : false;
  }

  get featureClasses(): string {
    if (!this.args.isSinglePlanPaywall) {
      return '';
    }
    return `flex gap-x-12 ${this.smallNumberOfFeatures ? 'flex-col' : 'flex-wrap'}`;
  }

  get prefix(): string | undefined {
    return this.planData?.prefix;
  }

  get quote(): Quote | undefined {
    if (this.args.plan.activeTrialIsGraduating) {
      return undefined;
    }
    if (this.planData?.addon) {
      return undefined;
    }
    if (!this.shouldUseQuoteService) {
      return undefined;
    }
    return this.quoteService.getQuoteById([this.args.plan.idAsNumber]);
  }

  get price(): Price | undefined {
    if (this.args.plan.activeTrialIsGraduating) {
      return undefined;
    }
    if (this.planData?.addon) {
      return undefined;
    }
    if (this.shouldUseQuoteService) {
      return this.quoteService.getQuoteById([this.args.plan.idAsNumber]);
    }
    if (this.args.plan.billableCustomerPlan) {
      return undefined;
    }
    return this.customerService.getPriceFromPlanIds([this.args.plan.idAsNumber]);
  }

  get seatPrice(): number {
    return this.quote?.fullSeatPrice(this.args.plan.idAsNumber) ?? 0;
  }

  get displayPrice(): string {
    return this.seatPrice ? `$${formatPriceValue(this.seatPrice)}` : '';
  }

  get startingFromText(): string | undefined {
    if (!this.price || this.planData.hideStartingFromText) {
      return undefined;
    }

    if (this.planData?.startingFromSeatText) {
      return this.intl.t(this.planData?.startingFromSeatText, {
        price: this.perUnitPriceFormatted || this.displayPrice,
      });
    } else {
      return this.intl.t(this.planData?.startingFromText, {
        price: `$${formatPriceValue(this.price.preDiscountAmount / 100)}`,
      });
    }
  }

  get perUnitPriceFormatted(): string | undefined {
    if (!this.price) {
      return '';
    }

    let charge = this.price.breakdown.firstObject.charges.find(
      (charge: any) => charge.isSeatMetric,
    );

    return charge ? `$${formatPriceValue(charge.per_unit_price / 100)}` : '';
  }

  get showDiscountedPrice(): boolean {
    return !!this.compellingOfferCampaignService.coreSeatDiscountForPlan(this.args.plan.id);
  }

  get displayDiscountedPrice(): string {
    if (!this.seatPrice) {
      return '';
    }
    let discounted = this.compellingOfferCampaignService.applyCoreSeatDiscount(
      this.args.plan.id,
      this.seatPrice,
    );
    return `$${formatPriceValue(discounted)}`;
  }

  @action
  selectCard() {
    if (this.isInteractive) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'non_graduated_plan_card',
        place: 'paywall',
        plan_id: this.args.plan.idAsNumber,
      });
      this.args.selectPlan(this.args.plan.idAsNumber);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Paywalls::Comparison::PlanCard': typeof PlanCardComponent;
    'paywalls/comparison/plan-card': typeof PlanCardComponent;
  }
}
